import { Story } from '../../types/story';

export const pragueJazzBand: Story = {
  id: 'prague-jazz-band',
  title: 'The Last Audition',
  excerpt: 'A retired accountant refuses to accept that his jazz dreams might be slightly out of tune with reality.',
  city: 'Prague',
  publishDate: '2025-12-13',
  readTime: '11 min read',
  tags: ['comedy', 'music', 'wholesome', 'jazz'],
  categories: ['romantic'],
  intensity: 0,
  content: `I've been rejected by seventeen jazz bands in Prague. Seventeen. You'd think at some point I'd take the hint, but I'm sixty-eight years old and I've spent forty-three years as an accountant. If there's one thing accountants are good at, it's persistence in the face of overwhelming evidence that we should stop.

The problem, according to literally everyone, is that I can't actually play the saxophone.

This is technically true, but I feel it's beside the point.

"Mr. Novák," the bandleader at the U Malého Glena said last week, "you're playing 'My Funny Valentine' in what I can only describe as the key of pain."

"It's experimental," I said.

"It's excruciating."

Fair enough. But that was band number sixteen, and I'm nothing if not optimistic.

Band number seventeen was yesterday. They played at a wine bar in Vinohrady, and I'd heard through the grapevine—well, through Karel at the corner shop who knows everyone—that their saxophonist had moved to Berlin.

Perfect timing.

I showed up at their rehearsal space with my saxophone case and my best tie. The one my wife bought me in 1987. She said it made me look distinguished. She's been dead for three years, but I still trust her judgment more than anyone else's.

"Gentlemen," I announced, walking into the cramped basement that smelled like beer and old carpet. "I understand you need a saxophonist."

The bandleader, a man in his forties with a ponytail and an expression that suggested he'd seen some things, looked up from his double bass.

"Do we know you?"

"Not yet. But you will. I'm František Novák, and I've been playing saxophone for..." I paused, doing the mental math. "Six months."

"Six months."

"Intensively."

He exchanged glances with the drummer, a woman with purple hair who was eating what appeared to be a very large sandwich.

"Can you play 'Autumn Leaves'?" she asked through a mouthful of ham.

"I can play anything," I said confidently. This was not true, but confidence is half the battle. The other half is actually being able to play, but I was working on that.

"Okay," the bandleader said slowly. "Show us."

I opened my case with a flourish. My saxophone gleamed under the fluorescent lights. I'd polished it that morning. It was the most expensive thing I owned, purchased with my retirement bonus. My daughter thought I'd lost my mind.

"Dad," she'd said, "you don't even like jazz."

"I'm learning to like it," I'd replied. "It's never too late to become interesting."

I assembled the saxophone, attached the reed, and took a deep breath. This was it. My moment.

I played the opening notes of "Autumn Leaves."

Or rather, I played what I believed were the opening notes of "Autumn Leaves." The saxophone made a sound like a goose being strangled by a slightly smaller goose.

"Stop," the bandleader said immediately. "Please stop."

"I'm just warming up."

"Please don't warm up."

The drummer had stopped eating her sandwich. This seemed significant.

"Mr. Novák," the bandleader said carefully, "have you considered... other hobbies?"

"I tried pottery. Made a bowl that looked like a hat. My grandson uses it for his toy cars."

"That's... nice?"

"The point is," I continued, "I'm committed to jazz. I practice every day. My neighbors have filed three noise complaints, but I'm undeterred."

"I can hear that," the drummer muttered.

"Look," the bandleader said, standing up. "I appreciate your enthusiasm. Really, I do. But we're playing a gig at the Jazz Dock next week, and we need someone who can, you know, play."

"I can play."

"You really can't."

"I'm improving."

"At what rate? Because if we're talking geological timescales, maybe we can work something out."

I deflated slightly. This was going like all the others. The problem was that I knew I wasn't good. I wasn't even mediocre. I was objectively terrible. But I'd spent forty-three years being good at something I hated, and now I wanted to be bad at something I loved.

"Listen," I said, sitting down on an amp. "I know I'm not good. But I'm sixty-eight years old. My wife died three years ago. My daughter lives in Germany. My grandson thinks I'm boring. And for forty-three years, I added up numbers in a gray office with gray walls and gray people."

The room was quiet except for the hum of the space heater.

"And then one day," I continued, "I was walking past the Jazz Dock, and I heard someone playing 'Take Five.' And I thought, 'That sounds like freedom.' So I bought a saxophone. And I'm terrible at it. But every time I play—even when it sounds like a dying animal—I feel alive."

The drummer and the bandleader looked at each other.

"That's actually kind of beautiful," the drummer said.

"It is," the bandleader agreed. "But you still can't join our band."

"I know."

"However," he said slowly, "there's a jam session every Tuesday at U Staré paní. It's for beginners. Very beginners. People who are still figuring out which end of the instrument to blow into."

"I know which end to blow into."

"That's a start. Go there. Practice. And maybe in a year or two, come back and audition again."

I stood up, packed my saxophone. "You really think I could be good enough in a year or two?"

"No," he said honestly. "But I think you'll have fun trying."

I left the rehearsal space and walked through Vinohrady as the sun set over the red rooftops. Prague in December is cold and beautiful, and I felt both.

That Tuesday, I went to U Staré paní. The jam session was in a back room that smelled like cigarettes and optimism. There were about fifteen people there, ranging from teenagers to a woman who looked about ninety.

"First time?" a young guy with a trumpet asked.

"Seventeenth time trying to join a band," I said. "First time admitting I need help."

He laughed. "I'm on my third year of trying. I'm almost mediocre now."

"That's the dream."

We played for two hours. It was terrible. Gloriously, joyfully terrible. Wrong notes, missed cues, rhythms that made no sense. The ninety-year-old woman played piano and kept yelling "Louder!" even though we were already too loud.

Afterward, we went to a pub around the corner. The trumpet player, whose name was Jakub, bought me a beer.

"So why jazz?" he asked.

"Why not jazz?"

"Fair point."

"What about you?"

"My dad played trumpet. He died when I was fifteen. Never got to teach me. So I'm teaching myself. Badly."

We clinked glasses.

"To being bad at things we love," I said.

"To being bad at things we love."

I've been going to that jam session every Tuesday for three months now. I'm still terrible. But I'm consistently terrible, which feels like progress. The ninety-year-old woman, whose name is Věra, says I've improved from "catastrophic" to "merely disastrous."

I'll take it.

My daughter called last week. "Dad, are you still doing that saxophone thing?"

"Every day."

"And you're still terrible?"

"Magnificently terrible."

She laughed. It was the first time I'd heard her laugh since her mother died.

"Good," she said. "Keep being terrible. It suits you."

Last Tuesday, Jakub asked if I wanted to try playing "Autumn Leaves" as a duet. Just the two of us, after everyone else had left.

We played it three times. It was bad. Really bad. But by the third time, there was a moment—just a brief, shining moment—where we were both on the same note at the same time, and it sounded almost like music.

"Did you hear that?" I asked.

"I heard it."

"We were terrible together."

"The best kind of terrible."

I'm sixty-eight years old. I've been rejected by seventeen jazz bands. I can't play the saxophone. My neighbors hate me. My grandson still thinks I'm boring.

But every Tuesday night, I walk through Prague with my saxophone case, and I feel like the most interesting person in the world.

Next week, Věra says we're going to try "Take Five."

I can't wait to ruin it.`
};
